import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useLang } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/api";
import {
  ArrowRight, Compass, ShieldCheck, Network, Lock, Info,
  AlertTriangle, Users, Gavel, ClipboardCheck, MessageSquare, GraduationCap, Download,
} from "lucide-react";

export default function Home() {
  const { t } = useLang();
  useEffect(() => { trackEvent("framework_started", { tool: "home" }); }, []);

  const pillars = [
    { icon: Compass, title: t.home.p1_title, desc: t.home.p1_desc },
    { icon: ShieldCheck, title: t.home.p2_title, desc: t.home.p2_desc },
    { icon: Network, title: t.home.p3_title, desc: t.home.p3_desc },
    { icon: Lock, title: t.home.p4_title, desc: t.home.p4_desc },
  ];

  const ctas = [
    { to: "/framework", label: t.cta.explore_framework, icon: Compass, primary: true, test: "cta-explore-framework" },
    { to: "/toolkit", label: t.cta.download_toolkit, icon: Download, test: "cta-download-toolkit" },
    { to: "/pilot", label: t.cta.join_pilot, icon: Users, test: "cta-join-pilot" },
    { to: "/feedback", label: t.cta.provide_feedback, icon: MessageSquare, test: "cta-provide-feedback" },
    { to: "/join", label: t.cta.request_training, icon: GraduationCap, test: "cta-request-training" },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="bg-mift-hero relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-24">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <span className="inline-block text-xs uppercase tracking-[0.2em] text-blue-300 bg-blue-500/10 border border-blue-500/30 px-3 py-1 rounded-full">
                {t.home.eyebrow}
              </span>
              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 leading-tight">
                {t.home.hero_title}
              </h1>
              <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">{t.home.hero_sub}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/framework" data-testid="hero-cta-framework" className="btn-primary inline-flex items-center gap-2">
                  {t.cta.explore_framework} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/toolkit" data-testid="hero-cta-toolkit" className="btn-ghost">
                  {t.cta.download_toolkit}
                </Link>
                <Link to="/pilot" data-testid="hero-cta-pilot" className="btn-ghost">
                  {t.cta.join_pilot}
                </Link>
              </div>
            </div>

            {/* Non-numeric implementation & reach panel */}
            <div className="lg:col-span-5">
              <div className="card-mift p-6" data-testid="home-implementation-panel">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-300 mb-3">
                  <Info className="w-3.5 h-3.5" /> {t.home.panel_title}
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">{t.home.panel_body}</p>
              </div>

              {/* Audience block */}
              <div className="card-mift p-6 mt-4" data-testid="home-audience-panel">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-300 mb-3">
                  <Users className="w-3.5 h-3.5" /> {t.home.audience_title}
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">{t.home.audience_body}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HAGUE / URGENCY WARNING */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-4">
        <div
          className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-5 flex items-start gap-3"
          data-testid="home-hague-urgency"
        >
          <AlertTriangle className="w-5 h-5 mt-0.5 text-amber-400 flex-shrink-0" />
          <div>
            <div className="font-semibold text-amber-100">{t.home.hague_urgency_title}</div>
            <p className="mt-1 text-sm text-amber-100/90 leading-relaxed">{t.home.hague_urgency_body}</p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
        <h2 className="text-2xl md:text-3xl font-semibold text-slate-100 max-w-3xl">
          {t.home.pillars_title}
        </h2>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {pillars.map((p, i) => {
            const Ic = p.icon;
            return (
              <div key={i} className="card-mift p-6 transition-transform hover:-translate-y-1" data-testid={`pillar-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-300">
                  <Ic className="w-5 h-5" />
                </div>
                <h3 className="mt-4 font-semibold text-slate-100">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* SCREENING + IMPLEMENTATION CORE COMPONENTS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-8 grid gap-6 lg:grid-cols-2">
        <div className="card-mift p-6" data-testid="home-screening-panel">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-amber-300">
            <ShieldCheck className="w-4 h-4" /> {t.home.screening_title}
          </div>
          <p className="mt-4 text-sm text-slate-300 leading-relaxed">{t.home.screening_body}</p>
          <Link
            to="/mediation"
            data-testid="home-screening-cta"
            className="mt-5 inline-flex items-center gap-2 text-sm text-blue-300 hover:text-blue-200 underline underline-offset-4"
          >
            {t.home.screening_cta} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="card-mift p-6" data-testid="home-implementation-block">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-300">
            <Gavel className="w-4 h-4" /> {t.home.implementation_title}
          </div>
          <p className="mt-4 text-sm text-slate-300 leading-relaxed">{t.home.implementation_body}</p>
          <Link
            to="/agreement"
            data-testid="home-implementation-cta"
            className="mt-5 inline-flex items-center gap-2 text-sm text-blue-300 hover:text-blue-200 underline underline-offset-4"
          >
            {t.home.implementation_cta} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* CORE CTAs — the 5 revised entry points */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
        <div className="max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-semibold text-slate-100">{t.home.quick_start_title}</h2>
          <p className="mt-3 text-slate-400 text-sm">{t.home.quick_start_sub}</p>
        </div>
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {ctas.map((c, i) => {
            const Ic = c.icon;
            return (
              <Link
                key={i}
                to={c.to}
                data-testid={c.test}
                className={`card-mift p-6 flex flex-col justify-between min-h-[160px] group ${
                  c.primary ? "border-blue-500/40" : ""
                }`}
              >
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-300">
                  <Ic className="w-5 h-5" />
                </div>
                <div className="mt-4 flex items-end justify-between gap-2">
                  <span className="font-medium text-slate-100 leading-tight">{c.label}</span>
                  <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-blue-300 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                </div>
              </Link>
            );
          })}
        </div>

        {/* Assessment secondary CTA row */}
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <Link to="/assessment" data-testid="home-secondary-assessment" className="card-mift p-5 flex items-center justify-between group">
            <div className="flex items-center gap-3">
              <ClipboardCheck className="w-5 h-5 text-blue-300" />
              <span className="font-medium text-slate-100">{t.cta.use_assessment}</span>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-blue-300 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/book" data-testid="home-secondary-book" className="card-mift p-5 flex items-center justify-between group">
            <div className="flex items-center gap-3">
              <Compass className="w-5 h-5 text-blue-300" />
              <span className="font-medium text-slate-100">{t.nav.book}</span>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-blue-300 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/research" data-testid="home-secondary-research" className="card-mift p-5 flex items-center justify-between group">
            <div className="flex items-center gap-3">
              <Network className="w-5 h-5 text-blue-300" />
              <span className="font-medium text-slate-100">{t.nav.research}</span>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-blue-300 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
